/**
 * Audit trail — US-A13.
 *
 * Every destructive or financial admin action records the actor, the action,
 * the target, the timestamp and before/after values.
 *
 * The client cannot insert into `audit_log`: there is no insert policy for it
 * (docs/03 §4.3, "the client never has an insert or update policy on
 * subscriptions, transactions or audit_log"). So this helper does not write the
 * row — it asks the edge function that performs the privileged action to write
 * it, or, for actions the admin performs through PostgREST directly, posts a
 * record request to the audit edge function which re-verifies the caller.
 *
 * AC3 — the log is append-only. Nothing in the admin offers an edit or delete;
 * the access matrix denies those actions for every role, including super_admin.
 */
import { invokeEdgeFunction } from '@/lib/supabase'

// T-004: supabase/functions/audit-record.
const AUDIT_FUNCTION = 'audit-record'

export type AuditAction =
  | 'subject.publish'
  | 'subject.unpublish'
  | 'subject.delete'
  | 'content.publish'
  | 'content.unpublish'
  | 'content.delete'
  | 'content.duplicate'
  | 'media.discard_upload'
  | 'user.deactivate'
  | 'user.ban'
  | 'user.unban'
  | 'user.warn'
  | 'subscription.grant'
  | 'subscription.revoke'
  | 'plan.deactivate'
  | 'plan.price_change'
  | 'notification.send'
  | 'cms.publish'
  | 'admin_user.create'
  | 'admin_user.deactivate'
  | 'admin_user.role_change'
  | 'report.resolve'
  | 'report.dismiss'
  | 'post.hide'
  | 'post.unhide'
  | 'post.delete'
  | 'reply.hide'
  | 'reply.unhide'
  | 'reply.delete'

export interface AuditEntryInput {
  action: AuditAction
  /** Table or domain object, e.g. `subjects`. */
  target: string
  targetId?: string | null
  before?: unknown
  after?: unknown
  /** Mandatory for grants, revocations, bans and deactivations. */
  reason?: string
}

/**
 * Records an admin action. Never throws into the caller's happy path: a failed
 * audit write is surfaced in the console and to monitoring, but it must not
 * roll back a user-visible action that already succeeded server-side. Actions
 * where the audit row must be atomic with the change (grants, revocations,
 * bans) are written by the same edge function that performs them and do not
 * call this helper at all.
 */
export async function recordAudit(entry: AuditEntryInput): Promise<void> {
  try {
    await invokeEdgeFunction<{ id: number }>(AUDIT_FUNCTION, {
      action: entry.action,
      target: entry.target,
      target_id: entry.targetId ?? null,
      before: entry.before ?? null,
      after: entry.after ?? null,
      reason: entry.reason ?? null,
    })
  } catch (error) {
    console.error('[audit] failed to record', entry.action, error)
  }
}

/** Narrow a record to the fields worth storing in before/after. */
export function auditDiff<T extends Record<string, unknown>>(
  before: T | undefined,
  after: T | undefined,
  fields: (keyof T)[],
): { before: Partial<T>; after: Partial<T> } {
  const pick = (source: T | undefined): Partial<T> => {
    if (!source) return {}
    return fields.reduce<Partial<T>>((accumulator, field) => {
      accumulator[field] = source[field]
      return accumulator
    }, {})
  }
  return { before: pick(before), after: pick(after) }
}
